import { localDrafts } from "../messaging/localDrafts";
import { continueFilteredScan, needsFilteredScan } from '../messaging/filteredConversationScan';
import { messagingOutbox } from "../messaging/outboxCoordinator";
import { publishUnreadCountChange } from "../messaging/unreadCountModel";
import { createConnectionRecovery, subscribeMessagingNetworkFailure } from "../messaging/connectionRecovery";
import { CHAT_MEMBERS_CHANGED_EVENT, refreshAgentEligibility } from "../messaging/agentEligibility";
import { createViewsPolicyRefresh, mergeViewsPolicy } from "../messaging/viewsPolicyRefresh";
import { recordMessagingRecovery } from "./messagingRecoveryDiagnostics";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, useRef, useSyncExternalStore, useLayoutEffect } from "react";
import { createDurablePilotCoordinator } from "../messaging/durablePilotCoordinator";
import { DURABLE_PILOT_ID, eventConversationId } from "../messaging/durablePilotContract";
import { triggerUnauthorized } from "../axiosSetup";
import { useSocket } from "./SocketContext";
import { useAuth } from "./AuthContext";
import { useAccess } from "../../features/access/context/AccessProvider";
import { PERMISSIONS } from "../../features/access/constants/permissionKeys";
import {
  canonicalMessagingReducer,
  createCanonicalMessagingState,
  hasRealParticipantName,
} from "../messaging/canonicalMessagingReducer";
import {
  fetchCanonicalChannelAccounts,
  fetchCanonicalConversation,
  fetchCanonicalConversationCount,
  fetchCanonicalConversations,
  fetchCanonicalConversationViews,
  fetchCanonicalMessages,
  fetchMessagingRecoverySnapshot,
  fetchMessagingRecoveryEvents,
  messagingV1ErrorTelemetry,
} from "../messaging/canonicalMessagingApi";
import { findCanonicalConversationByPhone } from "../messaging/canonicalConversationLookup";
import { subscribeOptimisticMessaging } from "../messaging/optimisticMessagingEvents";
import { subscribeCustomerTypeChanged } from "../messaging/customerProfileEvents";
import { perfEnabled, recordPipelineReduction } from '../qv4/performance/pipelinePerformance';
import { evictAttachmentPreviews } from '../messaging/attachmentPreviewCache';
import { evictLinkPreviews } from '../messaging/linkPreviewCache';
import { createSocketFrameQueue } from '../qv4/performance/socketFrameQueue';
import { messageAttachmentIds, RECENT_CONVERSATIONS_KEPT } from '../messaging/evictMessagingHistory';
import { useSharedOnlyMonitor } from '../../features/switchover/viewmodels/useSharedOnlyMonitor';
import { localSystemTimeline, mergeSystemTimeline } from '../../features/switchover/models/localSystemTimeline';
import { useMediaAnalysisStore } from '../../features/media-analysis/views/MediaAnalysisProvider';
import { createChannelReadGate } from '../messaging/channelReadGate';

const CanonicalMessagingContext = createContext(null);
const MessagingSubscriptionContext = createContext(null);

export function useMessagingSelector(selector, isEqual = Object.is, fallback) {
  const store = useContext(MessagingSubscriptionContext);
  const cache = useRef();
  const getSnapshot = useCallback(() => {
    if (!store) return fallback;
    const selected = selector(store.getSnapshot());
    if (cache.current && isEqual(cache.current.value, selected)) return cache.current.value;
    cache.current = { value: selected }; return selected;
  }, [store, selector, isEqual, fallback]);
  return useSyncExternalStore(store?.subscribe || emptySubscribe, getSnapshot, getSnapshot);
}
const emptySubscribe = () => () => {};

export function useCanonicalMessaging() {
  const value = useContext(CanonicalMessagingContext);
  if (!value) throw new Error("useCanonicalMessaging must be used inside CanonicalMessagingProvider.");
  return value;
}

/**
 * Authoritative read-side store for Messaging V1.
 *
 * Canonical commands live in messagingCommands; this provider owns reads and live state.
 */
export function CanonicalMessagingProvider({ children }) {
  const mediaAnalysis = useMediaAnalysisStore();
  const { socket, isConnected } = useSocket();
  const { authState } = useAuth();
  const { status: accessStatus, can: canAccess, isSuperuser, revision: accessRevision, permissions } = useAccess();
  const accessKey = JSON.stringify([authState?.isAuthenticated, authState?.id, accessRevision, isSuperuser,
    permissions?.slice().sort(), canAccess(PERMISSIONS.SALES_VIEW), canAccess(PERMISSIONS.SUPPLIER_CHATS_VIEW)]);
  const channelReads = useRef(createChannelReadGate());
  channelReads.current.access(accessKey);
  const loadedAccessRef = useRef(null);
  const conversationChannel = useCallback((id, hint) => {
    const conversation = stateRef.current.conversations[id];
    const known = conversation?.channel_account_id || conversation?.channel_account?.id || hint;
    if (known) return known;
    const message = (stateRef.current.messageOrderByConversation[id] || []).map(key => stateRef.current.messages[key]).find(item => item?.channel_account_id);
    return message?.channel_account_id;
  }, []);
  const canReadConversation = useCallback((id, hint) => channelReads.current.allows(id, conversationChannel(id, hint)), [conversationChannel]);
  const sharedOnly = useSharedOnlyMonitor(socket, isConnected, authState?.isAuthenticated, authState?.id,
    isSuperuser === true || canAccess('sales_pipeline.view') || canAccess('supplier_chats.view'));
  const sharedOnlyRef = useRef(sharedOnly); sharedOnlyRef.current = sharedOnly;
  const [state, setState] = useState(createCanonicalMessagingState);
  const [connectionState, setConnectionState] = useState({ state: "online", phase: null });
  const connectionRecoveryRef = useRef(null);
  useLayoutEffect(() => { localDrafts.session(authState?.id ?? null).catch(() => {}); }, [authState?.id]);
  const connectedRef = useRef(isConnected); connectedRef.current = isConnected;
  const [recoveryRevision, setRecoveryRevision] = useState(0);
  const [pilot, setPilot] = useState({ enabled: false, mode: "off" });
  const pilotRef = useRef(null);
  const pilotVisibleRef = useRef(false);
  const stateRef = useRef(state);
  const subscription = useRef(null);
  if (!subscription.current) {
    const listeners = new Set();
    let cachedState, cachedValue, snapshot;
    subscription.current = { value: null, getSnapshot: () => {
      if (cachedState !== stateRef.current || cachedValue !== subscription.current.value) {
        cachedState = stateRef.current; cachedValue = subscription.current.value;
        snapshot = { ...cachedValue, messages: cachedState.messages, conversations: cachedState.conversations,
          reactions: cachedState.reactions, messageOrderByConversation: cachedState.messageOrderByConversation,
          messageScopes: cachedState.apiHydration.messageScopes, conversationErrors: cachedState.apiHydration.conversationErrors };
      }
      return snapshot;
    },
      subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener); },
      notify: () => listeners.forEach(listener => listener()) };
  }
  const authenticatedRef = useRef(Boolean(authState?.isAuthenticated));
  const actorRef = useRef(authState);
  const activeConversationIdRef = useRef(null);
  const openConversationsRef = useRef(new Map());
  const recentlyViewedRef = useRef(new Map());
  const lastEvictionRef = useRef(0);
  const historySessionRef = useRef(null);
  const historySession = `${Boolean(authState?.isAuthenticated)}:${authState?.id}`;
  if (historySessionRef.current !== historySession) {
    historySessionRef.current = historySession;
    openConversationsRef.current.clear(); recentlyViewedRef.current.clear(); lastEvictionRef.current = 0;
  }
  const bufferedEventsRef = useRef([]);
  const messageFrameQueueRef = useRef(null);
  const bufferDepthRef = useRef(0);
  const generationRef = useRef(0);
  const loadedSessionRef = useRef(null);
  const pageLoadsRef = useRef(new Set());
  const filteredRequestRef = useRef({});
  const filterPagesRef = useRef(new Map());
  const messageLoadsRef = useRef(new Set());
  const conversationLoadsRef = useRef(new Map());
  const liveHydrationAttemptsRef = useRef(new Map());
  const liveNameReadsRef = useRef(new Set());
  const reconnectSeenRef = useRef(false);
  const previousConnectedRef = useRef(false);
  const recoveryWorkRef = useRef(null);
  const recoveredSyncRef = useRef(null);

  // Synchronous reduction is the commit acknowledgement for ordered replay.
  // React may batch rendering, but the cursor never precedes its matching state.
  const dispatch = useCallback(action => {
    const started = perfEnabled() ? performance.now() : 0;
    const before = stateRef.current;
    const next = canonicalMessagingReducer(before, action);
    const id = action.resource_id;
    if (next !== before && action.event_type?.startsWith("messaging.conversation.") && action.data?.resource?.viewer_state?.unread_count != null) {
      publishUnreadCountChange({ actor: actorRef.current?.id, previous: before.conversations[id], next: next.conversations[id] });
    }
    if (perfEnabled()) recordPipelineReduction(next, performance.now() - started);
    stateRef.current = next;
    setState(next);
    subscription.current.notify();
    return next;
  }, []);
  authenticatedRef.current = Boolean(authState?.isAuthenticated);
  actorRef.current = authState;
  useEffect(() => {
    messagingOutbox.session(authState?.isAuthenticated ? authState.id : null)
      .then(() => { if (connectedRef.current) messagingOutbox.flush().catch(() => {}); }).catch(() => {});
  }, [authState?.id, authState?.isAuthenticated]);
  useEffect(() => {
    const warn = event => { if (messagingOutbox.snapshot().rows.length) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);
  const updateCustomerType = useCallback(change => {
    if (authenticatedRef.current) dispatch({ ...change, type: "messaging/customer-type" });
  }, [dispatch]);
  const evictHistory = useCallback((force = false) => {
    if (!force && Date.now() - lastEvictionRef.current < 60000) return;
    lastEvictionRef.current = Date.now();
    const before = stateRef.current;
    const openIds = [...openConversationsRef.current.keys()];
    if (activeConversationIdRef.current) openIds.push(activeConversationIdRef.current);
    const recentIds = [...recentlyViewedRef.current.keys()].reverse().slice(0, RECENT_CONVERSATIONS_KEPT);
    const next = dispatch({ type: "messaging/evict-history", openIds, recentIds });
    evictAttachmentPreviews(Object.keys(before.attachments).filter(id => !next.attachments[id]));
    if (next !== before) {
      const retainedAttachments = new Set(Object.values(next.messages).flatMap(messageAttachmentIds));
      evictAttachmentPreviews(Object.values(before.messages).flatMap(messageAttachmentIds).filter(id => !retainedAttachments.has(id)));
      const urls = item => [item.content?.link_preview?.url, item.referral?.link_preview?.url].filter(Boolean);
      const retained = new Set(Object.values(next.messages).flatMap(urls));
      evictLinkPreviews(Object.values(before.messages).flatMap(urls).filter(url => !retained.has(url)));
    }
  }, [dispatch]);
  const trackConversation = useCallback(id => {
    if (!id) return () => {};
    const open = openConversationsRef.current;
    const session = historySessionRef.current;
    open.set(id, (open.get(id) || 0) + 1);
    const closeDraft = authState?.id != null ? localDrafts.open(id) : () => {};
    recentlyViewedRef.current.delete(id); recentlyViewedRef.current.set(id, true);
    if (recentlyViewedRef.current.size > 50) recentlyViewedRef.current.delete(recentlyViewedRef.current.keys().next().value);
    return () => { closeDraft(); if (session !== historySessionRef.current) return; if (open.get(id) > 1) open.set(id, open.get(id) - 1); else open.delete(id); evictHistory(); };
  }, [evictHistory, authState?.id]);
  useEffect(() => {
    const timer = setInterval(() => evictHistory(), 60000);
    const onHidden = () => { if (document.hidden) { messageFrameQueueRef.current?.flush(); evictHistory(true); } };
    document.addEventListener("visibilitychange", onHidden);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onHidden); };
  }, [authState?.id, authState?.isAuthenticated, evictHistory]);
  useEffect(() => subscribeCustomerTypeChanged(updateCustomerType), [updateCustomerType]);

  useEffect(() => {
    setPilot({ enabled: false, mode: "off" });
    if (!authState?.isAuthenticated) return undefined;
    const coordinator = createDurablePilotCoordinator({
      snapshot: fetchMessagingRecoverySnapshot, replay: fetchMessagingRecoveryEvents,
      history: fetchCanonicalMessages, getState: () => stateRef.current, dispatch,
      onChange: value => { setPilot(value); if (value.error?.status === 401) triggerUnauthorized(); },
      compatibility: async id => {
        const [conversation, messages] = await Promise.all([fetchCanonicalConversation(id), fetchCanonicalMessages(id, { limit: 100 })]);
        return { conversation, messages };
      },
    });
    pilotRef.current = coordinator;
    coordinator.setVisible(pilotVisibleRef.current && !document.hidden);
    const onFocus = () => coordinator.setVisible(pilotVisibleRef.current && !document.hidden && !["offline", "reconnecting", "gave_up"].includes(connectionRecoveryRef.current?.snapshot().state), { reopen: false });
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      coordinator.dispose();
      if (pilotRef.current === coordinator) pilotRef.current = null;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [authState?.isAuthenticated, authState?.id, dispatch]);

  const enablePilot = useCallback(() => pilotRef.current?.enable(), []);
  const refreshPilot = useCallback(() => pilotRef.current?.refresh(), []);
  const setPilotVisible = useCallback(value => {
    pilotVisibleRef.current = value;
    pilotRef.current?.setVisible(value && !document.hidden && !["offline", "gave_up"].includes(connectionRecoveryRef.current?.snapshot().state));
  }, []);

  useEffect(() => subscribeOptimisticMessaging(event => {
    if (!authenticatedRef.current) return;
    if (event.userId != null && event.userId !== actorRef.current?.id) return;
    const user = actorRef.current?.user || {};
    const displayName = [user.name, user.surname].filter(Boolean).join(" ") || user.username || "You";
    dispatch({ ...event, type: `messaging/optimistic-${event.type}`,
      actor: { actor_type: "agent", user_id: actorRef.current?.id ?? null, display_name: displayName } });
  }), [dispatch]);

  useEffect(() => {
    if (!socket) return undefined;
    const queue = createSocketFrameQueue();
    messageFrameQueueRef.current = queue;
    const sessionActor = authState?.id;
    const onMessagingEvent = event => {
      if (!authenticatedRef.current) return;
      if (pilotRef.current?.handleSocket(event)) return;
      if (pilotRef.current?.isActive() && eventConversationId(event, stateRef.current) === DURABLE_PILOT_ID
        && !/typing|presence/.test(event?.event_type || "")) return;
      if (bufferDepthRef.current) bufferedEventsRef.current.push(event);
      else queue.enqueue(() => { if (authenticatedRef.current && actorRef.current?.id === sessionActor) dispatch(event); });
    };
    socket.on("messaging.event", onMessagingEvent);
    return () => { socket.off("messaging.event", onMessagingEvent); queue.cancel(); if (messageFrameQueueRef.current === queue) messageFrameQueueRef.current = null; };
  }, [socket, dispatch, authState?.id]);

  // Someone changed who works chats: assignee lists and filters refetch.
  useEffect(() => {
    if (!socket) return undefined;
    const onMembersChanged = () => { if (authenticatedRef.current) refreshAgentEligibility().catch(() => {}); };
    socket.on(CHAT_MEMBERS_CHANGED_EVENT, onMembersChanged);
    return () => socket.off(CHAT_MEMBERS_CHANGED_EVENT, onMembersChanged);
  }, [socket]);

  useEffect(() => {
    if (!socket || !authState?.isAuthenticated) return undefined;
    const actor = authState.id;
    const coordinator = createViewsPolicyRefresh({
      getViews: () => stateRef.current.apiHydration.viewsByScope,
      fetchViews: async scope => {
        const generation = generationRef.current;
        const result = await fetchCanonicalConversationViews(scope);
        return { result, generation };
      },
      apply: (scope, { result, generation }) => {
        if (!authenticatedRef.current || actorRef.current?.id !== actor || generation !== generationRef.current) return;
        if (result.views_policy_version && result.views_policy_version === stateRef.current.apiHydration.viewsByScope[scope]?.views_policy_version) return;
        const viewsByScope = mergeViewsPolicy(stateRef.current.apiHydration.viewsByScope, { [scope]: result });
        const firstScope = Object.keys(stateRef.current.channelAccounts)[0];
        dispatch({ type: "messaging/api-metadata", viewsByScope, views: viewsByScope[firstScope]?.items || stateRef.current.apiHydration.views });
      },
    });
    socket.on("messaging.views.changed", coordinator.receive);
    return () => { coordinator.dispose(); socket.off("messaging.views.changed", coordinator.receive); };
  }, [socket, authState?.id, authState?.isAuthenticated, dispatch]);

  const withSocketBuffer = useCallback(async work => {
    messageFrameQueueRef.current?.flush();
    bufferDepthRef.current += 1;
    try {
      return await work();
    } finally {
      bufferDepthRef.current -= 1;
      if (!bufferDepthRef.current) {
        const events = bufferedEventsRef.current.splice(0);
        if (authenticatedRef.current) events.forEach(event => dispatch(event));
      }
    }
  }, [dispatch]);

  const recordError = useCallback((error, scope = {}) => {
    dispatch({ type: "messaging/api-error", error: { ...messagingV1ErrorTelemetry(error), ...scope } });
  }, [dispatch]);

  const refresh = useCallback(async ({ force = false } = {}) => {
    if (!authState?.isAuthenticated || accessStatus !== "ready"
      || (!canAccess(PERMISSIONS.SALES_VIEW) && !canAccess(PERMISSIONS.SUPPLIER_CHATS_VIEW))) return false;
    const sessionKey = String(authState.id);
    if (!force && loadedSessionRef.current === sessionKey && loadedAccessRef.current === accessKey) return true;
    const isNewSession = loadedSessionRef.current !== sessionKey;
    loadedSessionRef.current = sessionKey;
    loadedAccessRef.current = accessKey;
    const generation = ++generationRef.current;

    return withSocketBuffer(async () => {
      // Preserve data for same-user background reconciliations, but never let
      // one authenticated user's conversations survive an in-place user swap.
      if (isNewSession) { filterPagesRef.current.clear(); dispatch({ type: "messaging/reset" }); }
      dispatch({ type: "messaging/api-started", at: new Date().toISOString() });
      let succeeded = true;
      try {
        const channelAccounts = await fetchCanonicalChannelAccounts();
        if (generation !== generationRef.current) return false;
        // The endpoint itself returns only accounts visible to this actor.
        const scopes = channelAccounts;
        channelReads.current.accounts(channelAccounts);
        dispatch({ type: "messaging/api-metadata", channelAccounts,
          views: stateRef.current.apiHydration.views, viewsByScope: stateRef.current.apiHydration.viewsByScope });
        await Promise.all(scopes.map(async account => {
          const policy = await fetchCanonicalConversationViews(account.id);
          if (generation !== generationRef.current) return;
          const viewsByScope = mergeViewsPolicy(stateRef.current.apiHydration.viewsByScope, { [account.id || "default"]: policy });
          dispatch({ type: "messaging/api-metadata", viewsByScope, views: viewsByScope[scopes[0]?.id || "default"]?.items || [] });
          const { items: views, default_view: defaultView } = policy;
          const view = defaultView || views[0]?.key;
          if (!view) throw new Error("No chat view is available.");
          const scopeKey = account.id || "default";
          const baseParams = { view, ...(account.id ? { channel_account_id: account.id } : {}) };
          await Promise.all([
            fetchCanonicalConversations({ ...baseParams, limit: 100 }).then(page => {
              if (generation === generationRef.current) dispatch({ type: "messaging/api-conversations", scopeKey, params: { ...baseParams, limit: 100 }, items: page.items, page: page.page, reconcile: !isNewSession, append: false });
            }),
            fetchCanonicalConversationCount(baseParams).then(count => {
              if (generation === generationRef.current) dispatch({ type: "messaging/api-count", scopeKey, value: count });
            }),
          ]);
        }));
      } catch (error) {
        succeeded = false;
        if (generation === generationRef.current) recordError(error);
      } finally {
        if (generation === generationRef.current) dispatch({ type: "messaging/api-completed", at: new Date().toISOString() });
      }
      return succeeded;
    });
  }, [authState?.isAuthenticated, authState?.id, accessStatus, accessKey, canAccess, recordError, withSocketBuffer, dispatch]);

  useEffect(() => {
    if (!authState?.isAuthenticated) {
      generationRef.current += 1;
      loadedSessionRef.current = null;
      filterPagesRef.current.clear();
      bufferedEventsRef.current.splice(0);
      dispatch({ type: "messaging/reset" });
      return;
    }
    refresh().catch(recordError);
  }, [authState?.isAuthenticated, authState?.id, accessStatus, refresh, recordError, dispatch]);


  const loadMore = useCallback(async scopeKey => {
    const scope = stateRef.current.apiHydration.conversationScopes[scopeKey];
    if (!scope?.has_more || !scope.next_cursor || scope.searching_older || pageLoadsRef.current.has(scopeKey)) return false;
    pageLoadsRef.current.add(scopeKey);
    const requestVersion = filteredRequestRef.current[scopeKey];
    const generation = generationRef.current;
    try {
      return await withSocketBuffer(async () => {
        try {
          const page = await fetchCanonicalConversations({ ...scope.params, cursor: scope.next_cursor });
          if (generation !== generationRef.current || (scopeKey.startsWith("filtered:") && filteredRequestRef.current[scopeKey] !== requestVersion)) return false;
          dispatch({ type: "messaging/api-conversations", scopeKey, params: scope.params, items: page.items, page: { ...page.page, scan_paused: scope.scan_paused }, append: true });
          return true;
        } catch (error) {
          recordError(error);
          return false;
        }
      });
    } finally {
      pageLoadsRef.current.delete(scopeKey);
    }
  }, [recordError, withSocketBuffer, dispatch]);

  const loadAll = useCallback(async (scopeKey, signal, reset = false) => {
    const generation = generationRef.current;
    let scope = stateRef.current.apiHydration.conversationScopes[scopeKey];
    const requestVersion = filteredRequestRef.current[scopeKey];
    const seen = new Set();
    if (reset && scope && !signal?.aborted) {
      const first = await withSocketBuffer(async () => {
        const result = await fetchCanonicalConversations({ ...scope.params, cursor: undefined });
        if (!signal?.aborted && generation === generationRef.current && filteredRequestRef.current[scopeKey] === requestVersion) {
          dispatch({ type: "messaging/api-conversations", scopeKey, params: scope.params, items: result.items, page: result.page, append: false });
        }
        return result;
      });
      if (signal?.aborted || generation !== generationRef.current || filteredRequestRef.current[scopeKey] !== requestVersion) return false;
      scope = { ...scope, ...first.page };
    }
    while (scope?.has_more && scope.next_cursor && !signal?.aborted && generation === generationRef.current) {
      if (seen.has(scope.next_cursor)) break;
      seen.add(scope.next_cursor);
      const currentScope = scope;
      const page = await withSocketBuffer(async () => {
        const result = await fetchCanonicalConversations({ ...currentScope.params, cursor: currentScope.next_cursor });
        if (!signal?.aborted && generation === generationRef.current && filteredRequestRef.current[scopeKey] === requestVersion) {
          dispatch({ type: "messaging/api-conversations", scopeKey, params: currentScope.params, items: result.items, page: result.page, append: true });
        }
        return result;
      });
      if (signal?.aborted || generation !== generationRef.current || filteredRequestRef.current[scopeKey] !== requestVersion) return false;
      scope = { ...scope, ...page.page };
    }
    return !signal?.aborted;
  }, [withSocketBuffer, dispatch]);

  const findConversation = useCallback(async (phone, audience = "customer") => {
    const generation = generationRef.current;
    const item = await findCanonicalConversationByPhone(phone, audience, Object.values(stateRef.current.channelAccounts));
    if (item && generation === generationRef.current) dispatch({ type: "messaging/api-conversation-detail", item });
    return generation === generationRef.current ? item : null;
  }, [dispatch]);

  const loadMoreAll = useCallback(async () => {
    const keys = Object.keys(stateRef.current.apiHydration.conversationScopes).filter(key => !key.startsWith("filtered:"));
    const results = await Promise.all(keys.map(loadMore));
    return results.some(Boolean);
  }, [loadMore]);

  const loadFilteredConversations = useCallback(async (channelAccountId, filters = {}, { fresh = false } = {}) => {
    if (!channelAccountId) return false;
    const scopeKey = `filtered:${channelAccountId}`;
    const requestVersion = (filteredRequestRef.current[scopeKey] || 0) + 1;
    filteredRequestRef.current[scopeKey] = requestVersion;
    dispatch({ type: 'messaging/api-scan-state', scopeKey, searching: false });
    const params = { ...filters, channel_account_id: channelAccountId, limit: filters.limit || 100 };
    const previousScope = stateRef.current.apiHydration.conversationScopes[scopeKey];
    const cacheKey = value => JSON.stringify(Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b)));
    if (previousScope) {
      filterPagesRef.current.set(cacheKey(previousScope.params), {
        scope: previousScope, ids: stateRef.current.conversationOrderByScope[scopeKey] || [],
        excluded: stateRef.current.excludedConversationIdsByScope[scopeKey] || {},
        count: stateRef.current.apiHydration.counts[scopeKey],
      });
      // Bound membership snapshots; messages/resources remain in the shared store.
      if (filterPagesRef.current.size > 30) filterPagesRef.current.delete(filterPagesRef.current.keys().next().value);
    }
    const cached = filterPagesRef.current.get(cacheKey(params));
    if (cached && !fresh) dispatch({ type: "messaging/api-restore-scope", scopeKey, ...cached });
    const reconcile = Boolean(cached && !fresh) || JSON.stringify(previousScope?.params) === JSON.stringify(params);
    const generation = generationRef.current;
    const conversationReadBaseline = fresh ? stateRef.current.conversations : undefined;
    const current = () => generation === generationRef.current && filteredRequestRef.current[scopeKey] === requestVersion;
    // Counts are independent metadata, never a barrier to displaying page one.
    fetchCanonicalConversationCount(params).then(count => {
      if (current()) dispatch({ type: 'messaging/api-count', scopeKey, value: count });
    }).catch(error => { if (current()) recordError(error); });
    return withSocketBuffer(async () => {
      try {
        const page = await fetchCanonicalConversations(params);
        if (generation !== generationRef.current || filteredRequestRef.current[scopeKey] !== requestVersion) return false;
        dispatch({ type: "messaging/api-conversations", scopeKey, params, items: page.items, page: page.page, reconcile, append: false, conversationReadBaseline });
        if (needsFilteredScan(page.page)) {
          dispatch({ type: 'messaging/api-scan-state', scopeKey, searching: true });
          // Return after the first response; continuation must not hold the
          // sidebar's initial loading veil or buffer sockets for the whole scan.
          continueFilteredScan({ first: page,
            initialIds: reconcile ? stateRef.current.conversationOrderByScope[scopeKey] || [] : [], current,
            fetchNext: cursor => fetchCanonicalConversations({ ...params, cursor }),
            append: next => dispatch({ type: 'messaging/api-conversations', scopeKey, params, items: next.items,
              page: { ...next.page, searching_older: true }, append: true }),
          }).catch(error => { if (current()) recordError(error); })
            .finally(() => { if (current()) dispatch({ type: 'messaging/api-scan-state', scopeKey, searching: false, paused: true }); });
        }
        return true;
      } catch (error) {
        if (filteredRequestRef.current[scopeKey] === requestVersion) recordError(error);
        return false;
      }
    });
  }, [recordError, withSocketBuffer, dispatch]);

  const cancelFilteredConversationScan = useCallback(channelAccountId => {
    const scopeKey = `filtered:${channelAccountId}`;
    filteredRequestRef.current[scopeKey] = (filteredRequestRef.current[scopeKey] || 0) + 1;
    dispatch({ type: 'messaging/api-scan-state', scopeKey, searching: false });
  }, [dispatch]);

  const loadConversation = useCallback(async (conversationId, channelHint) => {
    if (!conversationId || !canReadConversation(conversationId, channelHint)) return null;
    const channel = conversationChannel(conversationId, channelHint);
    const ticket = channelReads.current.ticket();
    const generation = generationRef.current;
    const key = `${generation}:${conversationId}`;
    if (conversationLoadsRef.current.has(key)) return conversationLoadsRef.current.get(key);
    const request = Promise.resolve().then(() => fetchCanonicalConversation(conversationId)).then(item => {
      if (!item?.id || generation !== generationRef.current || !authenticatedRef.current || ticket !== channelReads.current.ticket()) return null;
      dispatch({ type: "messaging/api-conversation-detail", item });
      return item;
    }).catch(error => {
      if (generation === generationRef.current) {
        channelReads.current.refused(conversationId, channel, error, ticket);
        recordError(error, { conversationId, resource: "detail" });
      }
      return null;
    })
      .finally(() => conversationLoadsRef.current.delete(key));
    conversationLoadsRef.current.set(key, request);
    return request;
  }, [recordError, dispatch, canReadConversation, conversationChannel]);

  // First-contact socket patches often contain only an ID, preview and unread
  // counter. Hydrate that one conversation, not the entire sidebar. Scan the
  // accumulated live IDs so batched events for different chats aren't dropped.
  const liveConversationIds = useMemo(() => [...new Set([
    ...Object.keys(state.liveFieldPaths?.conversations || {}),
    ...(state.recentMessageEvents || []).map(event => state.messages[event.message_id]?.conversation_id).filter(Boolean),
  ])], [state.liveFieldPaths, state.recentMessageEvents, state.messages]);
  useEffect(() => {
    if (!authState?.isAuthenticated || !channelReads.current.ready()) return;
    liveConversationIds.forEach(id => {
      if (state.removedConversationIds?.[id]) return;
      const conversation = state.conversations[id];
      if (!canReadConversation(id)) return;
      const key = `${generationRef.current}:${id}`;
      const complete = conversation?.channel_account_id && conversation?.participant && conversation?.lifecycle_status;
      if (complete && hasRealParticipantName(conversation.participant)) return;
      // A first-contact patch may carry only the phone as the name: read the
      // detail once for the WhatsApp name, never poll a contact that has none.
      if (complete && liveNameReadsRef.current.has(key)) return;
      if (complete) liveNameReadsRef.current.add(key);
      const previous = liveHydrationAttemptsRef.current.get(key);
      if (previous && Date.now() - previous < 30000) return;
      liveHydrationAttemptsRef.current.set(key, Date.now());
      loadConversation(id);
    });
  }, [authState?.isAuthenticated, liveConversationIds, state.conversations, state.channelAccounts, state.removedConversationIds, loadConversation, canReadConversation]);

  const reconcileFilteredConversations = useCallback(async (channelAccountId, filters) => {
    const scopeKey = `filtered:${channelAccountId}`;
    const generation = generationRef.current;
    const version = filteredRequestRef.current[scopeKey];
    const [page, count] = await Promise.all([
      fetchCanonicalConversations({ ...filters, cursor: undefined }),
      fetchCanonicalConversationCount(filters),
    ]);
    if (generation !== generationRef.current || version !== filteredRequestRef.current[scopeKey]) return false;
    const scope = stateRef.current.apiHydration.conversationScopes[scopeKey];
    if (!scope) return false;
    // Add backend-confirmed matches while retaining every loaded page and its
    // continuation cursor. This must not restart the overview's bulk loader.
    dispatch({ type: "messaging/api-conversations", scopeKey, params: scope.params, items: page.items, page: page.page, reconcile: true });
    dispatch({ type: "messaging/api-count", scopeKey, value: count });
    return true;
  }, [dispatch]);

  const loadMessages = useCallback(async (conversationId, { reset = false, reconcile = false } = {}) => {
    if (!canReadConversation(conversationId)) return false;
    const ticket = channelReads.current.ticket();
    const channel = conversationChannel(conversationId);
    if (conversationId === DURABLE_PILOT_ID && pilotRef.current?.isActive()) {
      // A reopen/reconnect resumes replay; never replace durable state with a
      // provider-backed history response or restart its recovery checkpoint.
      if (reset) { await pilotRef.current.wake(); return true; }
      return pilotRef.current.loadOlder();
    }
    if (!conversationId || messageLoadsRef.current.has(conversationId)) return false;
    const generation = generationRef.current;
    const scope = reset ? null : stateRef.current.apiHydration.messageScopes[conversationId];
    if (scope && !scope.has_more) return false;
    messageLoadsRef.current.add(conversationId);
    try {
      return await withSocketBuffer(async () => {
        try {
          const page = await fetchCanonicalMessages(conversationId, { ...(scope?.next_cursor ? { cursor: scope.next_cursor } : {}), limit: 100 });
          if (generation !== generationRef.current || !authenticatedRef.current || ticket !== channelReads.current.ticket()) return false;
          // Start once per loaded page, before bubbles mount. Missing saved
          // results are cached too; this never blocks history rendering.
          mediaAnalysis?.checkPage(page.items.filter(item => item.kind === 'image' && item.content?.attachment?.id).map(item => item.id));
          const previous = stateRef.current.apiHydration.messageScopes[conversationId];
          dispatch({ type: "messaging/api-messages", conversationId, items: page.items,
            page: reconcile && previous ? previous : page.page, append: reconcile || Boolean(scope) });
          if (reset || !scope) {
            const resource = stateRef.current.conversations[conversationId];
            const audience = resource?.channel_account?.audience || stateRef.current.channelAccounts[resource?.channel_account_id]?.audience;
            const policy = sharedOnlyRef.current;
            // Do not hold compatibility loading or socket-buffer release while
            // the optional local page is in flight.
            localSystemTimeline(conversationId, policy.known && policy.enabled, audience).then(local => {
              if (generation === generationRef.current && authenticatedRef.current && sharedOnlyRef.current.enabled && local.length) {
                const existing = (stateRef.current.messageOrderByConversation[conversationId] || []).map(id => stateRef.current.messages[id]).filter(Boolean);
                const items = mergeSystemTimeline([...page.items, ...existing], local);
                dispatch({ type: 'messaging/api-messages', conversationId, items, page: stateRef.current.apiHydration.messageScopes[conversationId] || page.page, append: true });
              }
            }).catch(() => {});
          }
          return true;
        } catch (error) {
          if (generation === generationRef.current && authenticatedRef.current) {
            channelReads.current.refused(conversationId, channel, error, ticket);
            recordError(error, { conversationId, resource: "history" });
          }
          return false;
        }
      });
    } finally {
      messageLoadsRef.current.delete(conversationId);
    }
  }, [recordError, withSocketBuffer, dispatch, mediaAnalysis, canReadConversation, conversationChannel]);

  recoveryWorkRef.current = () => {
    pilotRef.current?.wake();
    refreshAgentEligibility().catch(recordError);
    const id = activeConversationIdRef.current;
    const openIds = [...new Set([...openConversationsRef.current.keys(), ...(id ? [id] : [])])].filter(id => canReadConversation(id));
    recordMessagingRecovery("recovery_started", { conversationId: id });
    // A sidebar failure must not prevent the visible drawer recovering.
    return Promise.all([
      refresh({ force: true }),
      ...openIds.flatMap(conversationId => [loadConversation(conversationId), loadMessages(conversationId, { reset: true, reconcile: true })]),
    ]).then(results => {
      // Filtered sidebars reconcile after recovery, not after every live event.
      setRecoveryRevision(value => value + 1);
      recordMessagingRecovery("recovery_settled", { conversationId: id });
      return results.every(result => result !== false && result !== null);
    })
      .catch(error => { recordMessagingRecovery("recovery_failed", { conversationId: id }); recordError(error); return false; });
  };

  const scheduleRecovery = useCallback(trigger => {
    recordMessagingRecovery("recovery_scheduled", { trigger });
    connectionRecoveryRef.current?.reconcile();
  }, []);
  const retryNow = useCallback(() => connectionRecoveryRef.current?.retryNow(), []);
  useEffect(() => {
    if (!authState?.isAuthenticated) { setConnectionState({ state: "online", phase: null }); return undefined; }
    const actor = authState.id;
    let browserOnline = navigator.onLine !== false;
    const controller = createConnectionRecovery({
      onChange: value => {
        setConnectionState(value);
        messagingOutbox.connection(value.state === "restored" && value.phase === "syncing" ? "reconnecting" : value.state);
        if (value.state === "gave_up") { socket?.io?.reconnection?.(false); socket?.disconnect?.(); }
        if (value.state === "online") socket?.io?.reconnection?.(true);
        if (value.state === "offline" || value.state === "gave_up") pilotRef.current?.setVisible(false);
        if (value.state === "online") pilotRef.current?.setVisible(pilotVisibleRef.current && !document.hidden);
      },
      recover: async () => {
        if (!authenticatedRef.current || actorRef.current?.id !== actor || !browserOnline || navigator.onLine === false) return false;
        if (!connectedRef.current) socket?.connect?.();
        const result = await recoveryWorkRef.current();
        if (result === false || !connectedRef.current || actorRef.current?.id !== actor) return false;
          messagingOutbox.connection("restored");
          await messagingOutbox.flush();
          return connectedRef.current && actorRef.current?.id === actor;
      },
    });
    connectionRecoveryRef.current = controller;
    const offline = () => { browserOnline = false; controller.lost(); };
    const online = () => { browserOnline = true; controller.reconcile(); };
    const focus = () => { if (!document.hidden) controller.wake(); };
    const unsubscribe = subscribeMessagingNetworkFailure(() => controller.lost());
    window.addEventListener("offline", offline); window.addEventListener("online", online);
    window.addEventListener("focus", focus); document.addEventListener("visibilitychange", focus);
    if (navigator.onLine === false) offline();
    controller.socket(connectedRef.current);
    return () => {
      controller.dispose(); unsubscribe();
      if (connectionRecoveryRef.current === controller) connectionRecoveryRef.current = null;
      window.removeEventListener("offline", offline); window.removeEventListener("online", online);
      window.removeEventListener("focus", focus); document.removeEventListener("visibilitychange", focus);
    };
  }, [authState?.id, authState?.isAuthenticated, socket]);
  useEffect(() => {
    connectionRecoveryRef.current?.socket(Boolean(isConnected));
    if (isConnected && reconnectSeenRef.current && !previousConnectedRef.current) scheduleRecovery("socket_reconnected");
    if (isConnected) reconnectSeenRef.current = true;
    previousConnectedRef.current = Boolean(isConnected);
  }, [isConnected, scheduleRecovery]);


  useEffect(() => {
    const sync = state.syncRequired;
    if (!sync || sync.event_id === recoveredSyncRef.current) return;
    if (!channelReads.current.ready()) return;
    recoveredSyncRef.current = sync.event_id;
    if (sync.scope === "conversation" && sync.conversation_id) {
      if (!canReadConversation(sync.conversation_id, sync.channel_account_id)) return;
      loadConversation(sync.conversation_id, sync.channel_account_id);
      loadMessages(sync.conversation_id, { reset: true });
    } else refresh({ force: true });
  }, [state.syncRequired, state.channelAccounts, loadConversation, loadMessages, refresh, canReadConversation]);

  const orderedConversations = useMemo(() => {
    const defaultScopes = Object.entries(state.conversationOrderByScope).filter(([key]) => !key.startsWith("filtered:"));
    const scopedIds = new Set(defaultScopes.flatMap(([, ids]) => ids));
    const hasHydratedScopes = defaultScopes.length > 0;
    const liveOnlyIds = liveConversationIds
      .filter(id => !scopedIds.has(id) && !defaultScopes.some(([key]) => state.excludedConversationIdsByScope?.[key]?.[id]));
    const visibleIds = hasHydratedScopes
      ? [...scopedIds, ...liveOnlyIds]
      : Object.keys(state.conversations);
    return visibleIds.map(id => state.conversations[id]).filter(Boolean).sort((left, right) => {
    const leftTime = Date.parse(left.last_activity_at || left.last_message?.occurred_at || 0) || 0;
    const rightTime = Date.parse(right.last_activity_at || right.last_message?.occurred_at || 0) || 0;
    return rightTime - leftTime;
    });
  }, [state.conversations, state.conversationOrderByScope, state.excludedConversationIdsByScope, liveConversationIds]);
  const hasMore = Object.entries(state.apiHydration.conversationScopes).some(([key, scope]) => !key.startsWith("filtered:") && scope?.has_more);

  const filteredConversationsByChannel = useMemo(() => Object.fromEntries(
    Object.entries(state.conversationOrderByScope)
      .filter(([key]) => key.startsWith("filtered:"))
      .map(([key, ids]) => [key.slice("filtered:".length), ids.map(id => state.conversations[id]).filter(Boolean)])
  ), [state.conversationOrderByScope, state.conversations]);

  const messagesForConversation = useCallback(conversationId => {
    const ids = stateRef.current.messageOrderByConversation[conversationId] || [];
    return ids.map(id => stateRef.current.messages[id]).filter(Boolean);
  }, []);

  const mergeLinkPreview = useCallback((messageId, path, url, preview) => {
    if (authenticatedRef.current) dispatch({ type: "messaging/api-link-preview", messageId, path, url, preview });
  }, [dispatch]);
  const value = useMemo(() => ({
    sharedOnly,
    trackConversation,
    connectionState, retryNow,
    updateCustomerType,
    recoveryRevision,
    pilot,
    enablePilot,
    refreshPilot,
    setPilotVisible,
    pilotComparisonState: state,
    activeConversationIdRef,
    currentAgentId: authState?.isAuthenticated ? authState.id : null,
    status: state.apiHydration.status,
    errors: state.apiHydration.errors,
    conversationErrors: state.apiHydration.conversationErrors || {},
    channelAccounts: Object.values(state.channelAccounts),
    views: state.apiHydration.views,
    viewsByScope: state.apiHydration.viewsByScope,
    counts: state.apiHydration.counts,
    conversations: state.conversations,
    orderedConversations,
    liveConversationIds,
    filteredConversationsByChannel,
    conversationScopes: state.apiHydration.conversationScopes,
    reactions: state.reactions,
    messages: state.messages,
    messageOrderByConversation: state.messageOrderByConversation,
    messageScopes: state.apiHydration.messageScopes,
    lastEvent: state.lastEvent,
    recentMessageEvents: state.recentMessageEvents,
    hasMore,
    refresh,
    loadMore,
    loadMoreAll,
    loadAll,
    findConversation,
    loadFilteredConversations,
    cancelFilteredConversationScan,
    reconcileFilteredConversations,
    loadConversation,
    loadMessages,
    messagesForConversation,
    mergeLinkPreview,
  }), [sharedOnly, connectionState, retryNow, trackConversation, mergeLinkPreview, updateCustomerType, recoveryRevision, pilot, enablePilot, refreshPilot, setPilotVisible, authState?.isAuthenticated, authState?.id, state, orderedConversations, liveConversationIds, filteredConversationsByChannel, hasMore, refresh, loadMore, loadMoreAll, loadAll, findConversation, loadFilteredConversations, cancelFilteredConversationScan, reconcileFilteredConversations, loadConversation, loadMessages, messagesForConversation]);

  subscription.current.value = value;
  useLayoutEffect(() => { subscription.current.notify(); }, [value]);

  return <MessagingSubscriptionContext.Provider value={subscription.current}><CanonicalMessagingContext.Provider value={value}>{children}</CanonicalMessagingContext.Provider></MessagingSubscriptionContext.Provider>;
}
